import { useMemo, useState } from 'react';
import { ShoppingBag, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { CartLine, MAX_LINE_QUANTITY, cartHasUnknownPrices, lineTotal } from '@/lib/ordering/cart';
import { ORDER_MODE_LABELS, OrderMode } from '@/lib/ordering/settings';
import { PRICE_TO_CONFIRM, buildOrderMessage, formatMoney, missingContactFields } from '@/lib/ordering/message';
import { buildWhatsappLink } from '@/lib/ordering/whatsapp';
import { trackOrderingEvent } from '@/lib/ordering/analytics';

interface OrderCartSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  restaurantName: string;
  whatsappDigits: string | null;
  menuUrl?: string;
  modes: OrderMode[];
  lines: CartLine[];
  total: number;
  onSetQuantity: (lineId: string, quantity: number) => void;
  onRemove: (lineId: string) => void;
  onSent?: () => void;
}

export function OrderCartSheet({
  open,
  onOpenChange,
  restaurantName,
  whatsappDigits,
  menuUrl,
  modes,
  lines,
  total,
  onSetQuantity,
  onRemove,
  onSent,
}: OrderCartSheetProps) {
  const [mode, setMode] = useState<OrderMode>(modes[0] ?? 'table');
  const [table, setTable] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [address, setAddress] = useState('');
  const [generalNote, setGeneralNote] = useState('');
  const [showErrors, setShowErrors] = useState(false);
  const [step, setStep] = useState<'cart' | 'checkout'>('cart');

  const activeMode: OrderMode = modes.includes(mode) ? mode : (modes[0] ?? 'table');
  const contact = useMemo(
    () => ({ mode: activeMode, table, customerName, address, generalNote }),
    [activeMode, table, customerName, address, generalNote]
  );
  const missing = missingContactFields(contact);
  const totalItems = lines.reduce((sum, line) => sum + line.quantity, 0);
  const hasUnknownPrices = cartHasUnknownPrices(lines);


  const goToCheckout = () => {
    if (lines.length === 0) return;
    trackOrderingEvent('begin_checkout', { lines: lines.length, items: totalItems });
    setStep('checkout');
  };

  const send = () => {
    if (lines.length === 0) return;
    if (!whatsappDigits) return;
    if (missing.length > 0) {
      setShowErrors(true);
      return;
    }
    const text = buildOrderMessage({ restaurantName, lines, contact, menuUrl });
    trackOrderingEvent('whatsapp_order_clicked', { lines: lines.length, mode: activeMode });
    window.open(buildWhatsappLink(whatsappDigits, text), '_blank', 'noopener,noreferrer');
    onSent?.();
  };


  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          setStep('cart');
          setShowErrors(false);
        }
        onOpenChange(next);
      }}
    >

      <SheetContent side="bottom" className="max-h-[92dvh] overflow-y-auto rounded-t-lg px-4">
        <SheetHeader className="pr-10 text-left">
          <SheetTitle className="flex items-center gap-2">
            <ShoppingBag className="h-5 w-5" aria-hidden="true" />
            Tu pedido
          </SheetTitle>
          <SheetDescription>
            Se envía por WhatsApp al restaurante. Ellos confirman disponibilidad y total final.
          </SheetDescription>
        </SheetHeader>

        {lines.length === 0 ? (
          <p className="py-10 text-center text-sm text-muted-foreground">
            Todavía no agregas platillos.
          </p>
        ) : (
          <div className="space-y-5 pb-6 pt-4">
            <ul className="space-y-3">
              {lines.map((line) => (
                <li key={line.lineId} className="rounded-lg border border-border p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-medium leading-tight">{line.itemName}</p>
                      {line.options.map((option) => (
                        <p key={option.optionId} className="text-xs text-muted-foreground">
                          {option.groupName}: {option.optionName}
                          {option.priceModifier > 0 ? ` (+${formatMoney(option.priceModifier)})` : ''}
                        </p>
                      ))}
                      {line.note && <p className="text-xs italic text-muted-foreground">Nota: {line.note}</p>}
                    </div>
                    <span className="shrink-0 text-right text-sm font-semibold tabular-nums">
                      {lineTotal(line) === null ? PRICE_TO_CONFIRM : formatMoney(lineTotal(line) as number)}
                    </span>
                  </div>
                  <div className="mt-3 flex items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      className="h-11 w-11 p-0"
                      aria-label={`Quitar uno de ${line.itemName}`}
                      onClick={() => onSetQuantity(line.lineId, line.quantity - 1)}
                    >
                      −
                    </Button>
                    <span className="min-w-8 text-center tabular-nums">{line.quantity}</span>
                    <Button
                      type="button"
                      variant="outline"
                      className="h-11 w-11 p-0"
                      aria-label={`Agregar uno de ${line.itemName}`}
                      disabled={line.quantity >= MAX_LINE_QUANTITY}
                      onClick={() => onSetQuantity(line.lineId, Math.min(MAX_LINE_QUANTITY, line.quantity + 1))}
                    >
                      +
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      className="ml-auto min-h-[44px] gap-1 text-muted-foreground"
                      onClick={() => onRemove(line.lineId)}
                      aria-label={`Quitar ${line.itemName} del pedido`}
                    >
                      <Trash2 className="h-4 w-4" aria-hidden="true" />
                      Quitar
                    </Button>
                  </div>
                </li>
              ))}
            </ul>

            <div className="flex items-center justify-between text-lg font-bold">
              <span>{hasUnknownPrices ? 'Total parcial' : 'Total estimado'}</span>
              <span className="tabular-nums">{formatMoney(total)}</span>
            </div>
            {hasUnknownPrices && (
              <p className="-mt-3 text-xs text-muted-foreground">
                Hay platillos con precio por confirmar: el restaurante te dirá el total final.
              </p>
            )}

            <Separator />

            {step === 'cart' ? (
              <Button
                type="button"
                className="min-h-[52px] w-full"
                onClick={goToCheckout}
              >
                Continuar con mis datos
              </Button>
            ) : (
              <>
                <fieldset className="space-y-2">
                  <legend className="text-sm font-medium">¿Cómo lo quieres?</legend>
                  <div className="flex flex-wrap gap-2">
                    {modes.map((option) => {
                      const isActive = option === activeMode;
                      return (
                        <Button
                          key={option}
                          type="button"
                          variant={isActive ? 'default' : 'outline'}
                          aria-pressed={isActive}
                          className="min-h-[44px]"
                          onClick={() => setMode(option)}
                        >
                          {ORDER_MODE_LABELS[option]}
                        </Button>
                      );
                    })}
                  </div>
                </fieldset>

                {activeMode === 'table' && (
                  <div className="space-y-2">
                    <Label htmlFor="order-table">Mesa (opcional)</Label>
                    <Input
                      id="order-table"
                      value={table}
                      maxLength={30}
                      onChange={(event) => setTable(event.target.value)}
                      placeholder="Mesa 4, Terraza…"
                      className="min-h-[44px]"
                    />
                  </div>
                )}

                {(activeMode === 'pickup' || activeMode === 'delivery') && (
                  <div className="space-y-2">
                    <Label htmlFor="order-name">Tu nombre</Label>
                    <Input
                      id="order-name"
                      value={customerName}
                      maxLength={80}
                      onChange={(event) => setCustomerName(event.target.value)}
                      placeholder="Nombre de quien recibe"
                      className="min-h-[44px]"
                    />
                  </div>
                )}

                {activeMode === 'delivery' && (
                  <div className="space-y-2">
                    <Label htmlFor="order-address">Dirección</Label>
                    <Textarea
                      id="order-address"
                      value={address}
                      maxLength={300}
                      onChange={(event) => setAddress(event.target.value)}
                      placeholder="Calle, número, colonia y referencias"
                      className="min-h-[72px]"
                    />
                    <p className="text-xs text-muted-foreground">
                      El restaurante confirma si llega a tu zona y el costo de envío.
                    </p>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="order-note">Nota para el restaurante (opcional)</Label>
                  <Textarea
                    id="order-note"
                    value={generalNote}
                    maxLength={300}
                    onChange={(event) => setGeneralNote(event.target.value)}
                    placeholder="Hora deseada, cubiertos, etc."
                    className="min-h-[64px]"
                  />
                </div>

                {showErrors && missing.length > 0 && (
                  <p role="alert" className="text-sm text-destructive">
                    Falta completar: {missing.join(', ')}.
                  </p>
                )}

                {!whatsappDigits && (
                  <p role="alert" className="text-sm text-destructive">
                    El restaurante no tiene un número de WhatsApp válido. Pide tu pedido en el local.
                  </p>
                )}

                <Button
                  type="button"
                  className="min-h-[52px] w-full bg-green-600 text-white hover:bg-green-700"
                  onClick={send}
                  disabled={!whatsappDigits}
                >
                  Enviar pedido por WhatsApp
                </Button>

                <Button
                  type="button"
                  variant="ghost"
                  className="min-h-[44px] w-full"
                  onClick={() => setStep('cart')}
                >
                  Volver al pedido
                </Button>
              </>
            )}

          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

export default OrderCartSheet;
